import { KeyboardEvent, ReactNode, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import BaseDirectory from "files/types/enums/baseDirectory";
import {
  DollarSign,
  AppWindow,
  Files,
  LayersPlus,
  LayoutDashboard,
  Package,
  Plus,
  SquareTerminal,
  Workflow,
} from "lucide-react";
import {
  useWorkflowsAccessFlag,
  useWorksheetsFlag,
} from "rwb/pages/project/hooks/usePipelinesFlag";
import { WorkflowSubmissionStrings } from "rwb/pages/project/types/classes/WorkflowSubmissionStrings";
import RoutePath from "rwb/pages/project/types/enums/routePath";
import { Skeleton } from "~/components/ui/skeleton";
import useUser from "~/contexts/user";
import { useFetcher } from "~/hooks/useFetcher";
import { useFliptFeature } from "~/hooks/useFliptFeature";
import { useProjectAppsFeature } from "~/hooks/useProjectAppsFeature";
import { cn } from "~/lib/utils";
import { SectionNavGroupLabel } from "~/pages/manifold-nextgen/components/SectionNavGroupLabel";
import {
  SectionNavLink,
  sectionNavLinkClassName,
} from "~/pages/manifold-nextgen/components/SectionNavLink";
import { SectionNavPlaceholder } from "~/pages/manifold-nextgen/components/SectionNavPlaceholder";
import { SectionSidebar } from "~/pages/manifold-nextgen/components/SectionSidebar";
import { SessionAgentIcon } from "~/pages/manifold-nextgen/components/agents/SessionAgentIcon";
import {
  nextgenConversationPath,
  nextgenProjectArtifactsPath,
  nextgenProjectAppPath,
  nextgenProjectChatsPath,
  nextgenProjectFeaturedSessionPath,
  nextgenProjectFilesPath,
  nextgenProjectPath,
  nextgenProjectSessionsPath,
  nextgenProjectCostsPath,
  nextgenProjectPipelinesPath,
  nextgenProjectWorkbenchPath,
} from "~/pages/manifold-nextgen/paths";
import { FLIPT_ROUTE_KEYS } from "~/router/constants";
import { Agent } from "~/services/agents";
import { type PublishedAppSummary, projectAppsPath } from "~/services/apps";
import { FeaturedSession, ProjectConversation } from "~/services/projects";
import { ConversationActionsMenu } from "./ConversationActionsMenu";
import { DeleteConversationDialog } from "./DeleteConversationDialog";
import { ShareConversationDialog } from "./ShareConversationDialog";
import { ComputeEnvsNavLabel } from "./compute/ComputeEnvsNavLabel";
import { sessionAgentSlots, type SessionAgentSlot } from "./sessionAgentSlots";
import { useFeaturedSessionServedAgents } from "./useFeaturedSessionAgents";
import { useFeaturedSessionsMutations } from "./useFeaturedSessionsMutations";
import { useSessionActions } from "./useSessionActions";

export interface ProjectSidebarProps {
  projectId: string;
  // The project's Research Workbench, or null for a project created before
  // provisioning existed — its workbench-backed views stay inert placeholders.
  workbenchId: number | null;
  sessions: ProjectConversation[] | undefined;
  featuredSessions: FeaturedSession[] | undefined;
  sessionAgentRoster: Agent[] | undefined;
  isOwner: boolean;
}

// How many sessions the sidebar shows before collapsing behind "View all".
const SESSIONS_SIDEBAR_LIMIT = 5;

interface SessionRowProps {
  projectId: string;
  session: ProjectConversation;
  // Resolved from `session.agent_ids` against the project roster by
  // `sessionAgentSlots`. An id off the roster (removed from the project,
  // deleted, or not viewable) renders the generic icon rather than dropping
  // out. `undefined` while the roster is still loading.
  slots: SessionAgentSlot[] | undefined;
  isRenaming: boolean;
  onStartRename: () => void;
  onCommitRename: (next: string) => void;
  onEndRename: () => void;
  onAutoRename: () => void;
  isAutoRenaming: boolean;
  onShare: () => void;
  onDelete: () => void;
  // Whether the current viewer authored this session — non-owners get no
  // rename/share/delete actions, since it isn't theirs to manage.
  isOwnSession: boolean;
}

const SessionRow = ({
  projectId,
  session,
  slots,
  isRenaming,
  onStartRename,
  onCommitRename,
  onEndRename,
  onAutoRename,
  isAutoRenaming,
  onShare,
  onDelete,
  isOwnSession,
}: SessionRowProps) => {
  const [draft, setDraft] = useState(session.display_name);
  const inputRef = useRef<HTMLInputElement>(null);

  const [flashing, setFlashing] = useState(false);
  const prevNameRef = useRef(session.display_name);

  useEffect(() => {
    if (prevNameRef.current === session.display_name) {
      return;
    }
    prevNameRef.current = session.display_name;
    setFlashing(true);
    const id = window.setTimeout(() => setFlashing(false), 900);
    return () => window.clearTimeout(id);
  }, [session.display_name]);

  useEffect(() => {
    if (isRenaming) {
      setDraft(session.display_name);
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [isRenaming, session.display_name]);

  const commit = () => {
    onCommitRename(draft);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onEndRename();
    }
  };

  const location = useLocation();
  const targetPath = nextgenConversationPath(projectId, session.id);
  const isActive = location.pathname === targetPath;

  return (
    <li className="group relative cursor-pointer">
      <NavLink
        to={targetPath}
        className={cn(
          "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium transition-colors",
          isActive
            ? "bg-black/[0.06] text-foreground dark:bg-white/[0.08]"
            : "text-muted-foreground hover:bg-black/[0.06] hover:text-foreground dark:hover:bg-white/[0.08]"
        )}
        onClick={(e) => {
          if (isRenaming) {
            e.preventDefault();
          }
        }}
      >
        <SessionAgentIcon slots={slots} size={18} withTooltip />
        <div className="min-w-0 flex-1">
          {isRenaming ? (
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={commit}
              onClick={(e) => e.preventDefault()}
              aria-label="Rename session"
              className="w-full rounded border border-border bg-background px-1 py-0 text-xs font-medium text-foreground outline-none focus:border-primary"
            />
          ) : (
            <span
              className={cn(
                "block truncate pr-4 text-sm font-medium transition-colors duration-700",
                flashing && "text-primary"
              )}
            >
              {session.display_name}
            </span>
          )}
        </div>
      </NavLink>

      {!isRenaming && isOwnSession && (
        <div className="absolute right-1 top-1/2 -translate-y-1/2">
          <ConversationActionsMenu
            onShare={onShare}
            onRename={onStartRename}
            onAutoRename={onAutoRename}
            isAutoRenaming={isAutoRenaming}
            onDelete={onDelete}
          />
        </div>
      )}
    </li>
  );
};

const RecentSessionsList = ({
  projectId,
  sessions,
  isOwner,
  currentUserEmail,
  featuredSessions,
  sessionAgentRoster,
}: {
  projectId: string;
  sessions: ProjectConversation[];
  isOwner: boolean;
  currentUserEmail: string | undefined;
  featuredSessions: FeaturedSession[] | undefined;
  sessionAgentRoster: Agent[] | undefined;
}) => {
  const {
    renamingId,
    startRename,
    commitRename,
    endRename,
    autoRenamingId,
    autoRename,
    actionTarget,
    setActionTarget,
    confirmDelete,
  } = useSessionActions(projectId);

  return (
    <>
      <ul className="flex flex-col gap-0.5" role="list">
        {sessions.map((session) => (
          <SessionRow
            key={session.id}
            projectId={projectId}
            session={session}
            slots={sessionAgentSlots(session.agent_ids, sessionAgentRoster)}
            isRenaming={renamingId === session.id}
            onStartRename={() => startRename(session.id)}
            onCommitRename={(next) => commitRename(session, next)}
            onEndRename={endRename}
            onAutoRename={() => void autoRename(session)}
            isAutoRenaming={autoRenamingId === session.id}
            onShare={() =>
              setActionTarget({
                session: { ...session, agentId: session.latest_agent_id },
                action: "share",
              })
            }
            onDelete={() => setActionTarget({ session, action: "delete" })}
            isOwnSession={session.user_id === currentUserEmail}
          />
        ))}
      </ul>

      <ShareConversationDialog
        open={actionTarget?.action === "share"}
        onOpenChange={(open) => {
          if (!open) {
            setActionTarget(null);
          }
        }}
        conversationId={actionTarget?.session.id ?? ""}
        projectId={projectId}
        isOwner={isOwner}
        displayName={actionTarget?.session.display_name ?? ""}
        agentId={actionTarget?.session.agentId}
        featuredSessions={featuredSessions}
      />

      <DeleteConversationDialog
        open={actionTarget?.action === "delete"}
        onOpenChange={(open) => {
          if (!open) {
            setActionTarget(null);
          }
        }}
        conversationTitle={actionTarget?.session.display_name ?? ""}
        onConfirm={confirmDelete}
      />
    </>
  );
};

interface FeaturedSessionRowProps {
  projectId: string;
  featuredSession: FeaturedSession;
  sessionAgentRoster: Agent[] | undefined;
  isOwner: boolean;
  onUnfeature: () => void;
}

// A rail row links to the read-only view scoped to this project, so the
// sidebar/layout stay in place — the rail carries only a share token, never
// a conversation id, so this renders from that token rather than routing
// through a conversation.
const FeaturedSessionRow = ({
  projectId,
  featuredSession,
  sessionAgentRoster,
  isOwner,
  onUnfeature,
}: FeaturedSessionRowProps) => {
  const agentIds = useFeaturedSessionServedAgents(
    featuredSession.share_token,
    featuredSession.agent_id
  );
  const slots = sessionAgentSlots(agentIds, sessionAgentRoster);

  return (
    <li className="group relative cursor-pointer">
      <Link
        to={nextgenProjectFeaturedSessionPath(projectId, featuredSession.share_token)}
        className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-black/[0.06] hover:text-foreground dark:hover:bg-white/[0.08]"
      >
        <SessionAgentIcon slots={slots} size={18} withTooltip />
        <span className="block min-w-0 flex-1 truncate pr-4">{featuredSession.display_name}</span>
      </Link>

      {isOwner && (
        <div className="absolute right-1 top-1/2 -translate-y-1/2">
          <ConversationActionsMenu onUnfeature={onUnfeature} />
        </div>
      )}
    </li>
  );
};

const FeaturedSessionsList = ({
  projectId,
  featuredSessions,
  sessionAgentRoster,
  isOwner,
}: {
  projectId: string;
  featuredSessions: FeaturedSession[];
  sessionAgentRoster: Agent[] | undefined;
  isOwner: boolean;
}) => {
  const { unfeature } = useFeaturedSessionsMutations(projectId);

  return (
    <ul className="flex flex-col gap-0.5" role="list">
      {featuredSessions.map((featuredSession) => (
        <FeaturedSessionRow
          key={featuredSession.id}
          projectId={projectId}
          featuredSession={featuredSession}
          sessionAgentRoster={sessionAgentRoster}
          isOwner={isOwner}
          onUnfeature={() => void unfeature(featuredSession)}
        />
      ))}
    </ul>
  );
};

/** The apps published to the project, each opened full width in the shell. */
const ProjectAppsNav = ({ projectId }: { projectId: string }) => {
  const { data: apps } = useFetcher<PublishedAppSummary[]>(projectAppsPath(projectId));

  return (
    <>
      <SectionNavGroupLabel>Apps</SectionNavGroupLabel>
      {apps === undefined ? (
        <ul className="flex flex-col gap-0.5" role="list" aria-busy="true">
          <li className="flex items-center gap-2 py-1.5">
            <Skeleton className="size-[18px] shrink-0 rounded-full" />
            <Skeleton className="h-3 w-28" />
          </li>
        </ul>
      ) : apps.length === 0 ? (
        <p className="py-1 text-xs italic text-muted-foreground/80">No published apps.</p>
      ) : (
        <ul className="flex flex-col gap-0.5" role="list">
          {apps.map((app) => (
            <ProjectNavLink
              key={app.id}
              to={nextgenProjectAppPath(projectId, app.slug)}
              icon={<AppWindow className="size-4" />}
              label={app.title}
            />
          ))}
        </ul>
      )}
    </>
  );
};

const ProjectNavLink = (props: {
  to: string;
  icon: ReactNode;
  label: ReactNode;
  end?: boolean;
  badge?: number;
}) => (
  <li>
    <SectionNavLink {...props} className="px-2 py-1.5" />
  </li>
);

const ProjectNavPlaceholder = ({ icon, label }: { icon: ReactNode; label: ReactNode }) => (
  <li>
    <SectionNavPlaceholder icon={icon} label={label} className="px-2 py-1.5" />
  </li>
);

export const ProjectSidebar = ({
  projectId,
  workbenchId,
  sessions,
  featuredSessions,
  sessionAgentRoster,
  isOwner,
}: ProjectSidebarProps) => {
  const currentUser = useUser();
  const { enabled: isProjectWorkflowsEnabled } = useFliptFeature(
    FLIPT_ROUTE_KEYS.PROJECT_WORKFLOWS
  );
  const isWorkflowsAccessEnabled = useWorkflowsAccessFlag();
  const isWorksheetsEnabled = useWorksheetsFlag();
  const { enabled: isProjectAppsEnabled } = useProjectAppsFeature();

  // Each workbench-backed view is a link once the project has a workbench, and
  // an inert placeholder otherwise — one ternary is the whole graceful
  // degradation for projects that predate provisioning.
  const workbenchNavItem = (view: string, icon: ReactNode, label: ReactNode) =>
    workbenchId === null ? (
      <ProjectNavPlaceholder icon={icon} label={label} />
    ) : (
      <ProjectNavLink
        to={nextgenProjectWorkbenchPath(projectId, workbenchId, view)}
        icon={icon}
        label={label}
      />
    );

  // Workflows and worksheets are scoped to the project itself, and stay
  // "Coming soon" until project workflows are on.
  const pipelineNavItem = (view: string, icon: ReactNode, label: string) =>
    isProjectWorkflowsEnabled ? (
      <ProjectNavLink to={nextgenProjectPipelinesPath(projectId, view)} icon={icon} label={label} />
    ) : (
      <ProjectNavPlaceholder icon={icon} label={label} />
    );

  return (
    <SectionSidebar ariaLabel="Project navigation">
      <ul className="flex flex-col gap-0.5 px-4 pt-4">
        <ProjectNavLink
          to={nextgenProjectPath(projectId)}
          end
          icon={<LayoutDashboard className="size-4" />}
          label="Overview"
        />
        <ProjectNavLink
          to={nextgenProjectArtifactsPath(projectId)}
          end
          icon={<Package className="size-4" />}
          label="Artifacts"
        />
        <ProjectNavLink
          to={`${nextgenProjectFilesPath(projectId)}/${BaseDirectory.PROJECT_FILES}`}
          icon={<Files className="size-4" />}
          label="Files"
        />
        {workbenchNavItem(
          RoutePath.ENVIRONMENTS,
          <SquareTerminal className="size-4" />,
          <ComputeEnvsNavLabel workbenchId={workbenchId} />
        )}
        {isWorkflowsAccessEnabled &&
          pipelineNavItem(
            `${RoutePath.WORKFLOWS}/${WorkflowSubmissionStrings.BASE_ROUTE}`,
            <Workflow className="size-4" />,
            "Workflows"
          )}
        {isWorksheetsEnabled &&
          pipelineNavItem(
            RoutePath.WORKSHEETS,
            <LayersPlus className="size-4" />,
            "Analysis Worksheets"
          )}
        {/* Project cost reporting is owner-only (the cost service rejects anyone else). */}
        {isOwner && (
          <ProjectNavLink
            to={nextgenProjectCostsPath(projectId)}
            end
            icon={<DollarSign className="size-4" />}
            label="Costs"
          />
        )}
      </ul>

      <div className="flex flex-1 flex-col overflow-y-auto px-4 pb-2 pt-3">
        {isProjectAppsEnabled && <ProjectAppsNav projectId={projectId} />}

        <SectionNavGroupLabel className={cn(isProjectAppsEnabled && "pt-4")}>
          Your Sessions
        </SectionNavGroupLabel>
        <NavLink
          to={nextgenProjectChatsPath(projectId)}
          end
          className={(state) => sectionNavLinkClassName(state, "mb-1 w-full px-2 py-1.5")}
        >
          <Plus className="size-4 shrink-0" />
          <span className="flex-1 text-left">New Session</span>
        </NavLink>
        {sessions === undefined ? (
          <ul className="flex flex-col gap-0.5" role="list" aria-busy="true">
            {Array.from({ length: 3 }).map((_, i) => (
              <li key={i} className="flex items-center gap-2 py-1.5">
                <Skeleton className="size-[18px] shrink-0 rounded-full" />
                <Skeleton className="h-3 w-28" />
              </li>
            ))}
          </ul>
        ) : sessions.length === 0 ? (
          <p className="py-1 text-xs italic text-muted-foreground/80">No sessions yet.</p>
        ) : (
          <>
            <RecentSessionsList
              projectId={projectId}
              sessions={sessions.slice(0, SESSIONS_SIDEBAR_LIMIT)}
              isOwner={isOwner}
              currentUserEmail={currentUser?.email}
              featuredSessions={featuredSessions}
              sessionAgentRoster={sessionAgentRoster}
            />
            {sessions.length > 0 && (
              <Link
                to={nextgenProjectSessionsPath(projectId)}
                className="mt-1 px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                View all
              </Link>
            )}
          </>
        )}

        <SectionNavGroupLabel className="pt-4">Featured Sessions</SectionNavGroupLabel>
        {featuredSessions === undefined ? (
          <ul className="flex flex-col gap-0.5" role="list" aria-busy="true">
            {Array.from({ length: 2 }).map((_, i) => (
              <li key={i} className="flex items-center gap-2 py-1.5">
                <Skeleton className="size-[18px] shrink-0 rounded-full" />
                <Skeleton className="h-3 w-28" />
              </li>
            ))}
          </ul>
        ) : featuredSessions.length === 0 ? (
          <p className="py-1 text-xs italic text-muted-foreground/80">No featured sessions.</p>
        ) : (
          <>
            <FeaturedSessionsList
              projectId={projectId}
              featuredSessions={featuredSessions.slice(0, SESSIONS_SIDEBAR_LIMIT)}
              sessionAgentRoster={sessionAgentRoster}
              isOwner={isOwner}
            />
            {featuredSessions.length > 0 && (
              <Link
                to={nextgenProjectSessionsPath(projectId)}
                state={{ view: "featured" }}
                className="mt-1 px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                View all
              </Link>
            )}
          </>
        )}
      </div>
    </SectionSidebar>
  );
};
